import { useCallback } from "react";
import { useClient, useMutation, useQuery } from "./urqlHooks";
import { refetchAllUsersByCompany } from "./company";

const CHECK_PASSWORD = `
mutation CHECK_PASSWORD($input: Login!) {
  checkPassword(input: $input)
}`;

export const CheckPassword = () => {
  const [result, executeMutation] = useMutation(CHECK_PASSWORD)
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation }
}

const CHANGE_PASSWORD = `
mutation CHANGE_PASSWORD($input: ChangePasswordInput!) {
  changeUserPassword(input: $input)
}`;

export const ChangePassword = () => {
  const [result, executeMutation] = useMutation(CHANGE_PASSWORD)
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation }
}


const USER = `query GET_USER_BY_ID($id: String!) {
  User(id: $id) {
      id
      name
      email
      roles
      blockedFeatures
      emails {
        address
        verified
      }
      profile {
        firstName
        lastName
        rut
        birthday
      }
      companyRole
      cashflowCollapsedCategories {
        companyId
        cashflowCategories
      }
      preferredLanguage
    }
}`

export const GetUserById = variables => {
  const [result, executeQuery] = useQuery({
    query: USER,
    variables: variables,
    pause: !variables?.id, // No ejecutar si no hay id
  })
  const { data, fetching, error } = result;
  return { data, fetching, error, executeQuery }
};

const GET_PAGINATED_USERS = `
query GET_USER_BY_COMPANY($companyId: String!, $pagination: PaginationInput!, $search: String) {
    paginatedUsersByCompany(companyId: $companyId, pagination: $pagination, search: $search) {
        totalDocs
        docs {
          email
          firstName
          lastName
          companyRole
        }
      }
}`;

export const GetPaginatedUsers = variables => {
  const [result, executeQuery] = useQuery({
    query: GET_PAGINATED_USERS,
    variables: variables,
  })
  const { data, fetching, error } = result;
  return { data, fetching, error, executeQuery }
};

const DELETE_USER = `
mutation REMOVE_USER_FROM_COMPANY($userEmail: String!, $companyId: String!) {
  removeUserFromCompany(userEmail: $userEmail, companyId: $companyId) {
    id
  }
}`;

export const DeleteUser = () => {
  const [result, executeMutation] = useMutation(DELETE_USER)
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation }
}

const UPDATE_USER = `
mutation EDIT_USER_COMPANY_ROLE(
  $userEmail: String!
  $companyId: String!
  $role: String!
) {
  editUserCompanyRole(userEmail: $userEmail, companyId: $companyId, role: $role) {
    id
  }
}`;

export const UpdateUser = () => {
  const [result, executeMutation] = useMutation(UPDATE_USER)
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation }
}
const ADD_USER_TO_COMPANY = `
  mutation ADD_USER_TO_COMPANY(
    $companyId: String!
    $email: String!
    $role: String!
    $name: String
    $lastName: String
    $isPepper3: Boolean
  ) {
    addUserToCompany(
      companyId: $companyId
      email: $email
      role: $role
      name: $name
      lastName: $lastName
      isPepper3: $isPepper3
    ) {
      id
    }
  }
`

export const AddUser = () => {
  const client = useClient();
  const [result, executeMutation] = useMutation(ADD_USER_TO_COMPANY);
  const { data, fetching, error } = result;
  const executeMutationWithMentionRefresh = useCallback(
    async (variables, context) => {
      const out = await executeMutation(variables, context);
      if (!out.error && out.data?.addUserToCompany && variables?.companyId) {
        refetchAllUsersByCompany(client, variables.companyId);
      }
      return out;
    },
    [client, executeMutation],
  );
  return { data, fetching, error, executeMutation: executeMutationWithMentionRefresh };
};


const USER_HAS_COMPANIES = `
mutation USER_HAS_COMPANIES($email: String!) {
  userHasCompanies(email: $email)
}`;

export const UserHasCompanies = () => {
  const [result, executeMutation] = useMutation(USER_HAS_COMPANIES)
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation }
};

const UPDATE_USER_INFO = `
mutation updateUserInfo(
  $id: String!
  $input: UserUpdateInput!
) {
  updateUser(id: $id, input: $input){
    id
    name
    email
    profile {
      firstName
      lastName
      rut
      birthday
    }
  }
}`;

export const UpdateUserInfo = () => {
  const [result, executeMutation] = useMutation(UPDATE_USER_INFO)
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation }
}

const GET_USER_ROLES = `
query GET_USER_ROLES {
  getUserRoles
}`;

export const GetUserRoles = () => {
  const [result, reExecuteQuery] = useQuery({
    query: GET_USER_ROLES,
  })
  const { data, fetching, error } = result;
  return { data, fetching, error, reExecuteQuery }
};

const UPDATE_CASHFLOW_COLLAPSED = `mutation UpdateCashflowCollapsedCategory($companyId: String! $categoryId: [String!]!){
  collapsedCashflow(companyId: $companyId, categoryId: $categoryId)
}`

export const UpdateCashflowCollapsed = () => {
  const [_result, updateCashflowCollapsed] = useMutation(UPDATE_CASHFLOW_COLLAPSED)
  return updateCashflowCollapsed
}

const GET_USER_FLOW_VIEW_BY_COMPANY_ID = `query getUserFlowViewByCompanyId($companyId: String!) { 
  getUserFlowViewByCompanyId(companyId: $companyId){
    companyId
    view
    showProjections
  }
}`

export const GetUserFlowViewByCompanyId = (variables) => {
  const [result, executeQuery] = useQuery({
    query: GET_USER_FLOW_VIEW_BY_COMPANY_ID,
    variables: variables,
  })
  const { data, fetching, error } = result;
  return { data, fetching, error, executeQuery }
};

const UPDATE_USER_FLOW_VIEW_BY_COMPANY_ID = `mutation updateUserFlowViewByCompanyId($companyId: String!, $input: UserFlowPrimaryViewInput!) {
  updateUserFlowViewByCompanyId(companyId: $companyId, input: $input){
    id
  }
}`

export const UpdateUserFlowViewByCompanyId = () => {
  const [result, executeMutation] = useMutation(UPDATE_USER_FLOW_VIEW_BY_COMPANY_ID)
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation }
}

const UPDATE_USER_PREFERRED_LANGUAGE = `
mutation updateUserPreferredLanguage($language: String!) {
  updateUserPreferredLanguage(language: $language) {
    id
    preferredLanguage
  }
}`

export const UpdateUserPreferredLanguage = () => {
  const [result, executeMutation] = useMutation(UPDATE_USER_PREFERRED_LANGUAGE)
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation }
}

// Query para obtener permisos actuales del usuario (desde admin panel)
const GET_MY_PERMISSIONS = `query GET_MY_PERMISSIONS {
  getMyPermissions {
    modulePermissions {
      moduleId
      hasAccess
      excludedFeatures
    }
    blockedFeatures
  }
}`

export const GetMyPermissions = () => {
  const [result, executeQuery] = useQuery({
    query: GET_MY_PERMISSIONS,
    requestPolicy: 'network-only', // Siempre obtener datos frescos
  })
  const { data, fetching, error } = result;
  return { data, fetching, error, executeQuery, reexecuteQuery: executeQuery }
}

const GET_USER_VERIFICATION = `
query getUserVerification($userId: String!) {
  getUserVerification(userId: $userId) {
    id
    email
    verified {
      isVerified
      verificationDate
    }
  }
}`;

export const GetUserVerification = (userId) => {
  const [result, executeQuery] = useQuery({
    query: GET_USER_VERIFICATION,
    variables: { userId },
    pause: !userId,
  })
  const { data, fetching, error } = result;
  return { data, fetching, error, executeQuery }
};

const UPDATE_USER_VERIFICATION = `
mutation updateUserVerification($userId: String!) {
  updateUserVerification(userId: $userId) {
    id
    verified {
      isVerified
      verificationDate
    }
  }
}`;

export const UpdateUserVerification = () => {
  const [result, executeMutation] = useMutation(UPDATE_USER_VERIFICATION)
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation }
}

const GET_MY_FAVORITE_ROUTES = `
query GET_MY_FAVORITE_ROUTES {
  getMyFavoriteRoutes {
    id
    label
    route
    metadata {
      savedAt
      pathname
      companyId
    }
  }
}`;

export const GetMyFavoriteRoutes = (options = {}) => {
  const [result, executeQuery] = useQuery({
    query: GET_MY_FAVORITE_ROUTES,
    requestPolicy: options.requestPolicy || 'cache-and-network',
    pause: options.pause,
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, executeQuery };
};

const ADD_FAVORITE_ROUTE = `
mutation ADD_FAVORITE_ROUTE($label: String!, $route: String!, $companyId: String) {
  addFavoriteRoute(label: $label, route: $route, companyId: $companyId) {
    id
    label
    route
    metadata {
      savedAt
      pathname
      companyId
    }
  }
}`;

export const AddFavoriteRoute = () => {
  const [result, executeMutation] = useMutation(ADD_FAVORITE_ROUTE);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const REMOVE_FAVORITE_ROUTE = `
mutation REMOVE_FAVORITE_ROUTE($favoriteRouteId: ID!) {
  removeFavoriteRoute(favoriteRouteId: $favoriteRouteId)
}`;

export const RemoveFavoriteRoute = () => {
  const [result, executeMutation] = useMutation(REMOVE_FAVORITE_ROUTE);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const GET_PROFILE_IMAGE_UPLOAD_URL = `
query GET_PROFILE_IMAGE_UPLOAD_URL($fileName: String!, $fileType: String!) {
  getProfileImageUploadUrl(fileName: $fileName, fileType: $fileType) {
    uploadUrl
    key
    imageId
  }
}`;

export async function requestProfileImageUploadUrl(client, { fileName, fileType }) {
  const result = await client
    .query(GET_PROFILE_IMAGE_UPLOAD_URL, { fileName, fileType })
    .toPromise();

  if (result.error) {
    throw new Error(
      result.error.graphQLErrors?.[0]?.message ||
        result.error.message ||
        "No se pudo obtener la URL de carga",
    );
  }

  const payload = result.data?.getProfileImageUploadUrl;
  if (!payload?.uploadUrl || !payload?.key) {
    throw new Error("Respuesta inválida del servidor (upload URL)");
  }

  return payload;
}

const SET_PROFILE_IMAGE = `
mutation SET_PROFILE_IMAGE($key: String!) {
  setProfileImage(key: $key) {
    id
    profile {
      avatar
    }
  }
}`;

export const SetProfileImage = () => {
  const [result, executeMutation] = useMutation(SET_PROFILE_IMAGE);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const PROFILE_AVATAR_URL = `
query PROFILE_AVATAR_URL {
  profileAvatarUrl {
    url
  }
}`;

export const GetProfileAvatarUrl = (options = {}) => {
  const [result, executeQuery] = useQuery({
    query: PROFILE_AVATAR_URL,
    requestPolicy: options.requestPolicy || "network-only",
    pause: options.pause,
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, executeQuery };
};
